import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../core/apiClient';
import apiClient from '../../core/apiClient';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Loader,
  Printer,
  Download,
  Mail,
  ChevronLeft,
  Check,
  X,
  AlertCircle,
  Send,
  PackageCheck,
  Ban,
  Building2,
  CalendarDays,
  FileText,
  Hash,
  CreditCard,
  UserRound,
  Truck,
  Clock,
  MapPin,
  MessageSquareText,
} from 'lucide-react';

const statusConfig: Record<string, { label: string; color: string }> = {
  DRAFT:             { label: 'Esborrany',    color: 'bg-gray-100 text-gray-700' },
  SENT:              { label: 'Enviat',       color: 'bg-indigo-100 text-indigo-800' },
  PARTIALLY_RECEIVED:{ label: 'Rebut Parcial', color: 'bg-orange-100 text-orange-800' },
  RECEIVED:          { label: 'Rebut',        color: 'bg-green-100 text-green-800' },
  CANCELLED:         { label: 'Cancel·lat',   color: 'bg-red-100 text-red-800' },
};

interface PurchaseOrder {
  id: string;
  orderNumber: string;
  otNumber?: string;
  orderDate: string;
  expectedDeliveryDate?: string;
  status: string;
  supplier: { id: string; name: string; email?: string; contactEmail?: string; activitatId?: number; activitat?: { id: number; name: string } };
  company: { name: string; address?: string; phone?: string; taxId?: string };
  lines: Array<{ id: string; description: string; material?: { description?: string } | null; quantity: number; quantityReceived?: number; unitPrice: number; discountPercent?: number; lineTotal?: number }>;
  project?: { name: string };
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  notes?: string;
  observations?: string;
  paymentMethod?: string;
  reference?: string;
  referenceUser?: { id: string; firstName: string; lastName: string; phone?: string; email?: string };
  shippingMethod?: string;
  deliveryTerm?: string;
  deliveryLocation?: string;
}

interface Supplier {
  id: string;
  name: string;
  email?: string;
  contactEmail?: string;
  activitatId?: number;
  supplierType?: string;
}

export function PurchaseOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{
    sent: string[];
    failed: Array<{ supplierId: string; error: string }>;
  } | null>(null);
  const [includeEsporadic, setIncludeEsporadic] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [receiveQuantities, setReceiveQuantities] = useState<Record<string, string>>({});
  const [receiving, setReceiving] = useState(false);

  useEffect(() => {
    loadOrderAndSuppliers();
  }, [id]);

  const loadOrderAndSuppliers = async () => {
    try {
      const [orderRes, suppliersRes] = await Promise.all([
        api.get(`/purchase-orders/${id}`),
        api.get('/suppliers/active'),
      ]);

      setOrder((orderRes as any).data);
      setSuppliers((suppliersRes as any).data || []);

      // Pre-select the supplier from the order
      if ((orderRes as any).data.supplier?.id) {
        setSelectedSuppliers([(orderRes as any).data.supplier.id]);
      }

      // Set default email subject based on status
      const isRFQ = (orderRes as any).data.status === 'DRAFT';
      setEmailSubject(isRFQ ? 'Sol·licitud d\'oferta de preus' : 'Comanda a proveïdor');
    } catch (error) {
      console.error('[v0] Error loading purchase order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      const response = await apiClient.get(`/purchase-orders/${id}/pdf`, {
        responseType: 'blob',
      });
      
      // Verificar que response.data es un Blob
      if (!(response.data instanceof Blob)) {
        throw new Error(`Expected Blob, got ${typeof response.data}`);
      }
      
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank');
      // Revoke after a short delay
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      console.error('[v0] Error printing PDF:', error);
      alert('Error opening PDF');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await apiClient.get(`/purchase-orders/${id}/pdf`, {
        responseType: 'blob',
      });
      
      // Verificar que response.data es un Blob
      if (!(response.data instanceof Blob)) {
        throw new Error(`Expected Blob, got ${typeof response.data}`);
      }
      
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comanda-${order?.orderNumber}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[v0] Error downloading PDF:', error);
      alert('Error downloading PDF');
    }
  };

  const handleSendEmail = async () => {
    if (selectedSuppliers.length === 0) {
      alert('Please select at least one supplier');
      return;
    }

    setSending(true);
    try {
      const result = await api.post(`/purchase-orders/${id}/email`, {
        supplierIds: selectedSuppliers,
        subject: emailSubject,
        message: emailMessage,
      });

      setSendResult((result as any).data);
      setTimeout(() => {
        setShowEmailDialog(false);
        loadOrderAndSuppliers();
      }, 2000);
    } catch (error) {
      console.error('[v0] Error sending email:', error);
      alert('Error sending email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const toggleSupplier = (supplierId: string) => {
    setSelectedSuppliers((prev) =>
      prev.includes(supplierId) ? prev.filter((id) => id !== supplierId) : [...prev, supplierId]
    );
  };

  const runAction = async (action: string, body?: Record<string, unknown>) => {
    setActionLoading(action);
    try {
      await api.post(`/purchase-orders/${id}/${action}`, body || {});
      await loadOrderAndSuppliers();
    } catch (error) {
      console.error(`[v0] Error running action "${action}":`, error);
      alert('No s\'ha pogut completar l\'acció. Torna-ho a provar.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAsSent = () => runAction('submit');

  const handleCancel = () => {
    if (!window.confirm('Segur que vols cancel·lar aquesta comanda?')) return;
    const reason = window.prompt('Motiu de la cancel·lació (opcional):') || undefined;
    runAction('cancel', { reason });
  };

  const pendingLines = (order?.lines || []).filter(
    (line) => Number(line.quantity) - Number(line.quantityReceived || 0) > 0
  );

  const handleOpenReceiveDialog = () => {
    if (pendingLines.length === 0) {
      alert('No queda quantitat pendent de rebre.');
      return;
    }
    const defaults: Record<string, string> = {};
    pendingLines.forEach((line) => {
      const pending = Number(line.quantity) - Number(line.quantityReceived || 0);
      defaults[line.id] = String(pending);
    });
    setReceiveQuantities(defaults);
    setShowReceiveDialog(true);
  };

  const handleConfirmReceive = async () => {
    const lines = pendingLines
      .map((line) => {
        const pending = Number(line.quantity) - Number(line.quantityReceived || 0);
        const qty = Number((receiveQuantities[line.id] || '0').replace(',', '.'));
        return { lineId: line.id, quantityReceived: Math.min(Math.max(qty, 0), pending) };
      })
      .filter((line) => line.quantityReceived > 0);

    if (lines.length === 0) {
      alert('Indica una quantitat a rebre per almenys una línia.');
      return;
    }

    setReceiving(true);
    try {
      await api.post(`/purchase-orders/${id}/receive`, { lines });
      setShowReceiveDialog(false);
      await loadOrderAndSuppliers();
    } catch (error) {
      console.error('[v0] Error receiving purchase order:', error);
      alert('No s\'ha pogut completar l\'acció. Torna-ho a provar.');
    } finally {
      setReceiving(false);
    }
  };

  if (loading) {
    return <Loader className="animate-spin" />;
  }

  if (!order) {
    return <div>Order not found</div>;
  }

  const isRFQ = order.status === 'DRAFT';
  const docTitle = isRFQ ? 'Sol·licitud d\'oferta de preus' : 'Comanda a proveïdor';

  // Segmentació per activitat i tipus de proveïdor al diàleg d'enviament
  const poActivitatId = order.supplier?.activitatId ?? null;
  const activityName = order.supplier?.activitat?.name ?? null;
  const filteredSuppliers = suppliers.filter((s) => {
    // Fallback: si el proveïdor de la comanda no té activitat, mostra tots
    if (!poActivitatId) return true;
    if (s.activitatId !== poActivitatId) return false;
    if (!includeEsporadic && s.supplierType === 'ESPORADIC') return false;
    return true;
  });

  const statusCfg = statusConfig[order.status] ?? { label: order.status, color: 'bg-gray-100 text-gray-700' };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="shrink-0">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-sm font-semibold text-foreground">{docTitle}</h1>
            <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{order.orderNumber}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={handlePrint} variant="outline" size="sm" className="gap-2">
          <Printer className="w-4 h-4" />
          Imprimir
        </Button>
        <Button onClick={handleDownloadPDF} variant="outline" size="sm" className="gap-2">
          <Download className="w-4 h-4" />
          Descarregar PDF
        </Button>
        <Button onClick={() => setShowEmailDialog(true)} size="sm" className="gap-2">
          <Mail className="w-4 h-4" />
          Enviar per correu
        </Button>

        {order.status === 'DRAFT' && (
          <Button onClick={handleMarkAsSent} disabled={!!actionLoading} size="sm" className="gap-2">
            <Send className="w-4 h-4" />
            Marcar com enviada
          </Button>
        )}

        {pendingLines.length > 0 && !['DRAFT', 'CANCELLED'].includes(order.status) && (
          <Button onClick={handleOpenReceiveDialog} disabled={!!actionLoading} size="sm" className="gap-2 bg-gray-600 hover:bg-gray-700">
            <PackageCheck className="w-4 h-4" />
            Rebre pendent
          </Button>
        )}

        {order.status === 'DRAFT' && (
          <Button onClick={handleCancel} variant="outline" disabled={!!actionLoading} size="sm" className="gap-2 text-red-600 border-red-200 hover:bg-red-50 ml-auto">
            <Ban className="w-4 h-4" />
            Cancel·lar
          </Button>
        )}
      </div>

      {/* Order Header Info */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Hash size={16} className="text-blue-600" /> Informació de la comanda
          </h2>
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Hash className="w-3 h-3" /> Número
            </p>
            <p className="font-semibold font-mono text-xs">{order.orderNumber}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <CalendarDays className="w-3 h-3" /> Data
            </p>
            <p className="font-semibold text-xs">{formatDate(order.orderDate)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Building2 className="w-3 h-3" /> Proveïdor
            </p>
            <p className="font-semibold text-xs">{order.supplier.name}</p>
          </div>
          {order.project && (
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <FileText className="w-3 h-3" /> Projecte
              </p>
              <p className="font-semibold text-xs">{order.project.name}</p>
            </div>
          )}
          {order.otNumber && (
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <Hash className="w-3 h-3" /> OT
              </p>
              <p className="font-semibold text-xs">{order.otNumber}</p>
            </div>
          )}
          {order.paymentMethod && (
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <CreditCard className="w-3 h-3" /> Forma de pagament
              </p>
              <p className="font-semibold text-xs">{order.paymentMethod}</p>
            </div>
          )}
          {(order.referenceUser || order.reference) && (
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <UserRound className="w-3 h-3" /> N/Referència
              </p>
              <p className="font-semibold text-xs">
                {order.referenceUser ? `${order.referenceUser.firstName} ${order.referenceUser.lastName}` : order.reference}
              </p>
            </div>
          )}
          {order.shippingMethod && (
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <Truck className="w-3 h-3" /> Forma de tramesa
              </p>
              <p className="font-semibold text-xs">{order.shippingMethod}</p>
            </div>
          )}
          {order.deliveryTerm && (
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <Clock className="w-3 h-3" /> Termini de lliurament
              </p>
              <p className="font-semibold text-xs">{order.deliveryTerm}</p>
            </div>
          )}
          {order.expectedDeliveryDate && (
            <div>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <CalendarDays className="w-3 h-3" /> Data prevista lliurament
              </p>
              <p className="font-semibold text-xs">{formatDate(order.expectedDeliveryDate)}</p>
            </div>
          )}
          {order.deliveryLocation && (
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <MapPin className="w-3 h-3" /> Lloc de lliurament
              </p>
              <p className="font-semibold text-xs">{order.deliveryLocation}</p>
            </div>
          )}
          {order.observations && (
            <div className="col-span-2 md:col-span-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <MessageSquareText className="w-3 h-3" /> Observacions
              </p>
              <p className="font-semibold text-xs whitespace-pre-wrap">{order.observations}</p>
            </div>
          )}
        </div>
      </div>

      {/* Lines Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FileText size={16} className="text-purple-600" /> Línies de la comanda
          </h2>
        </div>
        <div className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left py-3 px-6 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Descripció</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Quantitat</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Rebut</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Preu unit.</th>
                <th className="text-right py-3 px-6 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.lines?.map((line) => {
                const lineTotal = line.lineTotal ?? (line.quantity * line.unitPrice * (1 - (line.discountPercent || 0) / 100));
                const received = Number(line.quantityReceived || 0);
                const qty = Number(line.quantity || 0);
                const isFullyReceived = received >= qty && qty > 0;
                const isPartial = received > 0 && received < qty;
                return (
                  <tr key={line.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-6 font-medium">{line.description || line.material?.description || '—'}</td>
                    <td className="text-right py-3 px-4 tabular-nums">{qty.toLocaleString('ca-ES', { maximumFractionDigits: 3 })}</td>
                    <td className="text-right py-3 px-4 tabular-nums">
                      <span className={
                        isFullyReceived ? 'text-green-600 font-semibold' :
                        isPartial ? 'text-orange-600 font-semibold' : 'text-muted-foreground'
                      }>
                        {received.toLocaleString('ca-ES', { maximumFractionDigits: 3 })}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4 tabular-nums text-muted-foreground">{formatCurrency(line.unitPrice)}</td>
                    <td className="text-right py-3 px-6 tabular-nums font-medium">{formatCurrency(lineTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard size={16} className="text-green-600" /> Totals
          </h2>
        </div>
        <div className="p-5">
          <div className="flex justify-end items-end gap-8">
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Subtotal</p>
              <p className="font-semibold mt-0.5">{formatCurrency(order.subtotal)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">IVA ({order.taxRate}%)</p>
              <p className="font-semibold mt-0.5">{formatCurrency(order.taxAmount)}</p>
            </div>
            <div className="text-right border-l pl-8 ml-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">Total</p>
              <p className="text-2xl font-bold text-primary mt-0.5">{formatCurrency(order.total)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isRFQ ? 'Enviar sol·licitud d\'oferta de preus' : 'Enviar comanda al proveïdor'}
            </DialogTitle>
            <DialogDescription>
              Selecciona els proveïdors que vols que rebin aquesta comanda per email.
            </DialogDescription>
          </DialogHeader>

          {/* Capçalera d'activitat i checkbox esporàdics */}
          {!sendResult && (
            <div className="flex items-center justify-between gap-4 pb-1">
              {activityName && poActivitatId ? (
                <p className="text-xs text-muted-foreground">
                  Proveïdors de l&apos;activitat: <span className="font-semibold text-foreground">{activityName}</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">Tots els proveïdors actius</p>
              )}
              {poActivitatId && (
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={includeEsporadic}
                    onChange={(e) => setIncludeEsporadic(e.target.checked)}
                    className="rounded"
                  />
                  Incloure esporàdics
                </label>
              )}
            </div>
          )}

          {sendResult ? (
            <div className="space-y-4">
              {sendResult.sent.length > 0 && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex gap-2 items-start">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-green-800">Enviats correctament:</p>
                      {sendResult.sent.map((email) => (
                        <p key={email} className="text-sm text-green-700">
                          {email}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {sendResult.failed.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="flex gap-2 items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">Errors:</p>
                      {sendResult.failed.map((item) => (
                        <p key={item.supplierId} className="text-sm text-red-700">
                          {item.error}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold mb-3">Proveïdors:</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredSuppliers.map((supplier) => (
                    <label key={supplier.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectedSuppliers.includes(supplier.id)}
                        onChange={() => toggleSupplier(supplier.id)}
                        disabled={sending}
                      />
                      <span className="text-xs">
                        {supplier.name}
                        {supplier.email && <span className="text-muted-foreground"> ({supplier.email})</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-2">Assumpte</label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  disabled={sending}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-2">Nota addicional (opcional)</label>
                <Textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="S'afegirà com a nota addicional al text estàndard del correu (opcional)"
                  disabled={sending}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)} disabled={sending}>
              Tancar
            </Button>
            {!sendResult && (
              <Button onClick={handleSendEmail} disabled={sending || selectedSuppliers.length === 0}>
                {sending ? 'Enviant...' : 'Enviar'}
              </Button>
            )}
            {sendResult && <Button onClick={() => setShowEmailDialog(false)}>Fet</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={(open) => !receiving && setShowReceiveDialog(open)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Rebre material</DialogTitle>
            <DialogDescription>
              Indica la quantitat rebuda de cada línia. Per defecte es mostra la quantitat pendent, però pots
              modificar-la per registrar una recepció parcial.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {pendingLines.map((line) => {
              const pending = Number(line.quantity) - Number(line.quantityReceived || 0);
              return (
                <div key={line.id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      {line.description || line.material?.description || '—'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Pendent: {String(pending).replace('.', ',')} de {String(Number(line.quantity)).replace('.', ',')}
                    </p>
                  </div>
                  <Input
                    type="number"
                    min={0}
                    max={pending}
                    step="0.0001"
                    value={receiveQuantities[line.id] ?? ''}
                    onChange={(e) =>
                      setReceiveQuantities((prev) => ({ ...prev, [line.id]: e.target.value }))
                    }
                    disabled={receiving}
                    className="w-28 text-right"
                  />
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiveDialog(false)} disabled={receiving}>
              Cancel·lar
            </Button>
            <Button onClick={handleConfirmReceive} disabled={receiving}>
              {receiving ? 'Registrant...' : 'Confirmar recepció'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PurchaseOrderDetail;
